import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import type { Article, DatanormCatalog } from '../types';

export default function ArticlesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [catalogId, setCatalogId] = useState('');
  const [page, setPage] = useState(1);
  const [showUpload, setShowUpload] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: catalogs = [] } = useQuery<DatanormCatalog[]>({
    queryKey: ['datanorm-catalogs'],
    queryFn: () => api.get('/datanorm/catalogs').then((r) => r.data),
  });

  const { data: articlesData, isLoading } = useQuery<{ articles: Article[]; total: number }>({
    queryKey: ['datanorm-articles', search, catalogId, page],
    queryFn: () =>
      api.get('/datanorm/articles', {
        params: { search: search || undefined, catalogId: catalogId || undefined, page, limit: 50 },
      }).then((r) => r.data),
  });

  const articles = articlesData?.articles || [];
  const total = articlesData?.total || 0;
  const totalPages = Math.ceil(total / 50);

  const importMutation = useMutation({
    mutationFn: (formData: FormData) =>
      api.post('/datanorm/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['datanorm-catalogs'] });
      qc.invalidateQueries({ queryKey: ['datanorm-articles'] });
      setShowUpload(false);
    },
  });

  const deleteCatalog = useMutation({
    mutationFn: (id: string) => api.delete(`/datanorm/catalogs/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['datanorm-catalogs'] });
      qc.invalidateQueries({ queryKey: ['datanorm-articles'] });
      if (catalogId) setCatalogId('');
    },
  });

  const handleUpload = () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    importMutation.mutate(fd);
  };

  const fmt = (n: number) => Number(n).toFixed(2).replace('.', ',');

  return (
    <div>
      <div className="sp-page-header">
        <div>
          <h1 className="sp-page-title">Artikelstamm</h1>
          <p className="sp-page-subtitle">
            {catalogs.length} Kataloge &middot; {total} Artikel
          </p>
        </div>
        <button onClick={() => setShowUpload(!showUpload)} className="sp-btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          DATANORM Import
        </button>
      </div>

      {/* Upload Card */}
      {showUpload && (
        <div className="sp-form-card">
          <h3 className="sp-form-title">DATANORM Katalog importieren</h3>
          <p className="text-xs text-slate-400 mb-4">
            Unterstützte Formate: DATANORM v4 und v5 (Textdateien mit Semikolon-Trennung)
          </p>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="sp-label">Datei wählen</label>
              <input ref={fileRef} type="file" accept=".dat,.txt,.001,.002,.003,.004,.005"
                className="sp-input text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100" />
            </div>
            <button onClick={handleUpload} disabled={importMutation.isPending} className="sp-btn-primary">
              {importMutation.isPending ? 'Importieren...' : 'Importieren'}
            </button>
            <button onClick={() => setShowUpload(false)} className="sp-btn-secondary">Abbrechen</button>
          </div>
          {importMutation.isError && (
            <p className="mt-3 text-xs text-error-500">Import fehlgeschlagen. Bitte Dateiformat prüfen.</p>
          )}
          {importMutation.isSuccess && (
            <p className="mt-3 text-xs text-emerald-600">Import erfolgreich abgeschlossen.</p>
          )}
        </div>
      )}

      {/* Kataloge */}
      {catalogs.length > 0 && (
        <div className="sp-card mb-6 animate-fade-in">
          <h3 className="text-[13px] font-semibold text-slate-700 mb-3">Kataloge</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setCatalogId(''); setPage(1); }}
              className={`sp-badge cursor-pointer transition-all ${!catalogId ? 'bg-brand-50 text-brand-700 border border-brand-200' : 'bg-surface-100 text-slate-500 hover:bg-surface-200'}`}
            >
              Alle
            </button>
            {catalogs.map((c) => (
              <div key={c.id} className="flex items-center gap-1">
                <button
                  onClick={() => { setCatalogId(c.id); setPage(1); }}
                  className={`sp-badge cursor-pointer transition-all ${catalogId === c.id ? 'bg-brand-50 text-brand-700 border border-brand-200' : 'bg-surface-100 text-slate-500 hover:bg-surface-200'}`}
                >
                  {c.supplier} — {c.name} ({c._count?.articles || 0})
                </button>
                <button
                  onClick={() => { if (confirm(`Katalog "${c.name}" und alle Artikel löschen?`)) deleteCatalog.mutate(c.id); }}
                  className="sp-btn-icon !w-6 !h-6 hover:!text-error-500 hover:!bg-error-50"
                  title="Katalog löschen"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suche */}
      <div className="sp-search">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input placeholder="Artikel suchen (Beschreibung, Artikelnr.)..." value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
      </div>

      {/* Tabelle */}
      <div className="sp-card overflow-hidden animate-fade-in">
        <table className="sp-table">
          <thead>
            <tr>
              <th>Artikelnr.</th>
              <th>Beschreibung</th>
              <th>Einheit</th>
              <th className="text-right">Preis (EUR)</th>
              <th>Preisgruppe</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5}><div className="flex items-center justify-center py-12 gap-2 text-slate-400">
                <svg className="sp-spinner" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                Laden...</div></td></tr>
            ) : articles.length === 0 ? (
              <tr><td colSpan={5}><div className="sp-empty">
                <div className="w-12 h-12 rounded-2xl bg-surface-100 flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                </div>
                <span className="text-sm font-medium">
                  {search ? 'Keine Artikel gefunden' : 'Noch keine Artikel importiert'}
                </span>
                {!search && <span className="text-xs text-slate-400 mt-1">DATANORM-Datei importieren um Artikel hinzuzufügen</span>}
              </div></td></tr>
            ) : articles.map((a) => (
              <tr key={a.id}>
                <td className="font-mono text-xs font-semibold text-slate-800">{a.datanormId}</td>
                <td>
                  <div>
                    <span className="text-slate-700">{a.description}</span>
                    {a.longText && <span className="block text-[11px] text-slate-400 line-clamp-1">{a.longText}</span>}
                  </div>
                </td>
                <td className="text-slate-500 text-xs">{a.unit}</td>
                <td className="text-right font-mono text-xs font-semibold text-slate-800">{fmt(a.price)}</td>
                <td>
                  {a.priceGroup && (
                    <span className="sp-badge bg-surface-100 text-slate-500">{a.priceGroup}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-surface-100">
            <span className="text-xs text-slate-400">
              Seite {page} von {totalPages} ({total} Artikel)
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="sp-btn-ghost text-xs disabled:opacity-30"
              >
                Zurück
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="sp-btn-ghost text-xs disabled:opacity-30"
              >
                Weiter
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
